import { G } from '@mobily/ts-belt';
import { Box, EmptyState } from '@stitch-fix/mode-react';
import { useClientContext } from '../../utils/contexts/client-context-provider';
import { useWardrobeCopy } from '../../utils/contexts/copy-provider';
import { useRedirect } from '../../utils/hooks/useRedirect';
import { useWardrobeParams } from '../../utils/hooks/useWardrobeParams';

/**
 * Getting the redirect path from when a user removes the last saved item on a page.
 * If there are in Stock saved items from previous pages then it will re-direct
 * to the last previous page. If the current page is a 1 or 0 then it will re-direct
 * to saved-items to check if there are any OOS items.
 */
const getRedirectSavedItemsPath = (wardrobePageNumber: number) => {
  if (wardrobePageNumber <= 1) {
    return '/wardrobe/saved-items';
  }
  return `/wardrobe/saved-items/?showAvailableItems=true&page=${wardrobePageNumber - 1}`;
};

/**
 * The `NoItems` component renders the initial/empty state for the Bought and Saved Items OOS pages.
 * The copy and illustrations change depending on which page, and businessline the client is on.
 */
const NoItems = () => {
  const {
    clientBusinessLine
  } = useClientContext();
  const {
    wardrobePage,
    wardrobeParams
  } = useWardrobeParams();
  const {
    copy
  } = useWardrobeCopy();
  const boughtPage = wardrobePage === 'bought-items';
  const isInStockSavedItemsPage = !boughtPage && wardrobeParams.showAvailableItems;

  // Render the Empty state when the wardrobe page number is either a 1 or 0
  const canRenderContent = !isInStockSavedItemsPage && wardrobeParams.page < 2;

  // Saved Items Empty state should only render when the wardrobeParams hasShowAvailableItems
  // as false, because we want to check if the user has any OOS saved items before rendering an empty state.
  // By redirecting to the saved-items page we are able to re-request OOS saved items to check.
  useRedirect({
    shouldRedirect: isInStockSavedItemsPage,
    path: getRedirectSavedItemsPath(wardrobeParams.page)
  });
  const bodyCopy = boughtPage ? copy.boughtModuleEmptyStateSubheading : copy.savedModuleEmptyStateSubheading;
  const headingCopy = boughtPage ? copy.boughtModuleEmptyStateHeading : copy.savedModuleEmptyStateHeading;
  if (G.isUndefined(clientBusinessLine)) {
    return null;
  }
  return canRenderContent ? <EmptyState mx="auto" mt={4} heading={headingCopy} primaryAction={boughtPage ? {
    as: 'a',
    href: '/client/scheduled',
    children: 'Schedule A Fix'
  } : {
    as: 'a',
    href: '/shop',
    children: 'Shop Now'
  }} secondaryAction={boughtPage ? {
    as: 'a',
    href: '/shop/looks/trending',
    children: 'Shop Personalized Looks'
  } : undefined // Saved page doesn't have a secondary CTA
  }>
      {bodyCopy}
    </EmptyState> : <Box />;
};
export default NoItems;